import type { GroupLifeQuoteDetails } from '@/services/group-life';
import {
  isTerminalPortalStatus,
  PortalStatus,
} from '@/shared/enums/portal-status.enum';
import { useIsUnderwriterUser } from '@/shared/hooks';
import { Button, Spinner, Text } from '@noorden-insurtech/alba-design-system';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft, FileText, ScrollText } from 'lucide-react';
import { useCallback } from 'react';
import { usePmeIncompleteLivesGuard } from '../../../hooks/pme/use-pme-incomplete-lives-guard.hook';
import { usePmeProposalDetailsViewModel } from '../../../hooks/pme/use-pme-proposal-details-view-model.hook';
import { usePmeProposalResumeViewModel } from '../../../hooks/pme/use-pme-proposal-resume-view-model.hook';
import type { PmeProposalStepProps } from '../proposal.types';
import {
  isPmeProposalAwaitingTransmission,
  isPmeSignatureBlockedByAnalysis,
  PME_PROPOSAL_IN_ANALYSIS_SIGNATURE_BLOCKED_REASON,
} from '../utils/pme-proposal-lock.util';

const AWAITING_TRANSMISSION_NOTICE =
  'Todas as assinaturas foram concluídas e a proposta é transmitida automaticamente. Se ela continuar Em assinatura, use Atualizar todos.';
import { PmeIncompleteLivesDialog } from './sections/pme-incomplete-lives-dialog.component';
import { PmeProposalDetailsSections } from './sections/pme-proposal-details-sections.component';
import { PmeProposalSignaturesSection } from './sections/pme-proposal-signatures-section.component';

type Props = PmeProposalStepProps & {
  policyId: string;
  details?: GroupLifeQuoteDetails;
  productCode?: string;
  proposalNumber?: string;
  effectiveDate?: string;
  onSentToAnalysis?: () => void;
};

/**
 * Etapa de ASSINATURA da proposta PME — mesmo desenho da assinatura do Capital
 * Global: o detalhamento completo da proposta, os documentos em coleta abaixo e
 * um rodapé com "Voltar" à esquerda e as ações à direita.
 *
 * É AQUI que a assinatura digital é disparada, pelo botão "Assinatura Digital"
 * (como no `CapitalGlobalProposalResumeFooter`). O Confirmar do Resumo só traz
 * o corretor até esta tela; nenhuma chamada ao DocuSign sai de lá.
 *
 * Não há botão de transmitir: com proposta e carta assinadas o backend transmite
 * sozinho (webhook da DocuSign ou "Atualizar todos").
 *
 * O PME assina somente digital — a assinatura física foi descartada do produto.
 */
export function StepPmeSignature({
  onBack,
  canGoBack,
  policyId,
  details,
  productCode,
  proposalNumber,
  effectiveDate,
  onSentToAnalysis,
}: Props) {
  const navigate = useNavigate();
  const { isUnderwriterUser } = useIsUnderwriterUser();

  const {
    hasSignaturesStarted,
    allCompleted,
    proposalEnvelope,
    commercialLetterEnvelope,
    canRetryProposal,
    canRetryCommercialLetter,
    canSyncAny,
    isSyncPending,
    isRetryPending,
    isConfirmPending,
    isGenerateProposalPdfPending,
    isGenerateCommercialLetterPdfPending,
    isLoadingSignatures,
    handleGenerateProposalPdf,
    handleGenerateCommercialLetterPdf,
    handleConfirm,
    handleSyncAll,
    handleRetry,
    isBrokerActionBlocked,
    brokerActionBlockedReason,
  } = usePmeProposalResumeViewModel({
    policyId,
    proposalNumber,
    details,
    effectiveDate,
    onSentToAnalysis,
  });

  const detailsSections = usePmeProposalDetailsViewModel({
    details,
    productCode,
    effectiveDate,
    hasSignaturesStarted,
  });
  /**
   * Vida sem CPF ou sem nome não vai para assinatura — o documento assinado
   * sairia com vida incompleta. O Resumo já barra no Confirmar; aqui é a
   * segunda linha de defesa, porque é aqui que o documento nasce.
   */
  const { guard: hasIncompleteLives, dialogProps: incompleteLivesDialogProps } =
    usePmeIncompleteLivesGuard(detailsSections.lives);

  const handleDigitalSignature = useCallback(() => {
    if (hasIncompleteLives()) return;
    handleConfirm();
  }, [handleConfirm, hasIncompleteLives]);

  // Sem cálculo não há o que imprimir — os PDFs sairiam sem prêmio.
  const isCalculated = Boolean(details?.calculation);
  const isTransmitted =
    Number(details?.statusPortal) === PortalStatus.ProposalTransmitted;
  const isUnderAnalysis =
    Number(details?.statusPortal) === PortalStatus.QuoteUnderAnalysis ||
    Number(details?.statusPortal) === PortalStatus.ProposalInAnalysis;

  /**
   * As ações de assinatura só somem quando não há mais assinatura a fazer —
   * concluída, proposta já transmitida, ou encerrada (cancelada, expirada,
   * recusada). Mesma regra do Capital Global (`shouldHideSignatureActions`).
   */
  const isTerminal = isTerminalPortalStatus(details?.statusPortal);
  const shouldHideSignatureActions =
    isTerminal || (hasSignaturesStarted && allCompleted) || isTransmitted;
  const isSignatureBlockedByAnalysis = isPmeSignatureBlockedByAnalysis({
    statusPortal: details?.statusPortal,
  });
  const isAwaitingTransmission = isPmeProposalAwaitingTransmission({
    statusPortal: details?.statusPortal,
    hasSignaturesStarted,
    allSignaturesCompleted: allCompleted,
  });

  if (isLoadingSignatures) {
    return (
      <div className='flex items-center justify-center py-16'>
        <Spinner size='lg' colorScheme='blue' />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-10'>
      {/* Vidas só leitura: a massa já foi conferida no Resumo e alterá-la aqui
          invalidaria o documento que está sendo assinado. */}
      <PmeProposalDetailsSections
        sections={detailsSections}
        policyId={policyId}
        details={details}
        proposalNumber={proposalNumber}
        isUnderAnalysis={isUnderAnalysis}
        showUnderwritingFields={isUnderwriterUser === true}
        canManageLives={false}
      />

      {/* A seção não renderiza nada sem envelope — sem aviso a tela ficaria
          muda justamente antes de o corretor disparar a assinatura. */}
      {!hasSignaturesStarted && (
        <div className='flex flex-col gap-1'>
          <Text className='text-xl font-semibold text-alba-gray-900'>
            Assinaturas digitais
          </Text>
          <Text className='text-sm text-alba-gray-600'>
            Use a Assinatura Digital para enviar a proposta e a carta comercial
            ao estipulante e ao corretor via DocuSign.
          </Text>
        </div>
      )}

      {/* Sem `isReadOnlyView` no sync: o status salvo só muda pelo sync ou pelo
          webhook, e é por ele que a proposta assinada é transmitida. Com tudo
          assinado e a proposta ainda em 12, o sync continua aqui — é a nova
          tentativa de transmitir quando a do webhook não aconteceu. */}
      <PmeProposalSignaturesSection
        proposalEnvelope={proposalEnvelope}
        commercialLetterEnvelope={commercialLetterEnvelope}
        canRetryProposal={canRetryProposal && !isTerminal}
        canRetryCommercialLetter={canRetryCommercialLetter && !isTerminal}
        canSyncAny={(canSyncAny || isAwaitingTransmission) && !isTerminal}
        notice={
          isAwaitingTransmission ? AWAITING_TRANSMISSION_NOTICE : undefined
        }
        isSyncPending={isSyncPending}
        isRetryPending={isRetryPending}
        onSyncAll={handleSyncAll}
        onRetry={handleRetry}
        actionsDisabled={isBrokerActionBlocked}
        actionsDisabledReason={brokerActionBlockedReason}
      />

      <PmeIncompleteLivesDialog {...incompleteLivesDialogProps} />

      {/* Rodapé no desenho do Capital Global: Voltar à esquerda, ações à
          direita — Carta Comercial, Gerar PDF, Assinatura Digital e Sair. */}
      <div className='flex flex-row items-center justify-between gap-6'>
        <Button
          type='button'
          variant='ghost'
          size='lg'
          className='flex items-center gap-2'
          onClick={onBack}
          disabled={!canGoBack}
        >
          <ArrowLeft className='h-5 w-5' strokeWidth={1.6} />
          Voltar
        </Button>
        <div className='flex flex-wrap items-center justify-end gap-2'>
          <Button
            type='button'
            variant='ghost'
            size='md'
            className='flex items-center gap-2'
            onClick={handleGenerateCommercialLetterPdf}
            disabled={
              isGenerateCommercialLetterPdfPending ||
              !isCalculated ||
              isBrokerActionBlocked
            }
            title={brokerActionBlockedReason}
            loading={isGenerateCommercialLetterPdfPending}
          >
            <ScrollText className='h-5 w-5' strokeWidth={1.6} />
            Carta Comercial
          </Button>
          <Button
            type='button'
            size='md'
            className='flex items-center gap-2 rounded-xl bg-alba-green-500 px-8 py-2 text-alba-white-500 hover:bg-alba-green-600'
            onClick={handleGenerateProposalPdf}
            disabled={
              isGenerateProposalPdfPending ||
              !isCalculated ||
              isBrokerActionBlocked
            }
            title={brokerActionBlockedReason}
            loading={isGenerateProposalPdfPending}
          >
            <FileText className='h-5 w-5' strokeWidth={1.6} />
            Gerar PDF
          </Button>
          {!shouldHideSignatureActions && (
            <Button
              type='button'
              variant='primary'
              size='md'
              className='rounded-xl px-8 py-2'
              onClick={handleDigitalSignature}
              // `isViewMode` NÃO entra: em "Em assinatura" (12) a proposta está
              // travada para edição, que é exatamente o estado em que o
              // corretor chega aqui para assinar. Travar por isso deixaria o
              // fluxo sem saída.
              disabled={
                isConfirmPending ||
                !isCalculated ||
                isBrokerActionBlocked ||
                isSignatureBlockedByAnalysis
              }
              title={
                isSignatureBlockedByAnalysis
                  ? PME_PROPOSAL_IN_ANALYSIS_SIGNATURE_BLOCKED_REASON
                  : brokerActionBlockedReason
              }
              loading={isConfirmPending}
            >
              Assinatura Digital
            </Button>
          )}
          <Button
            type='button'
            variant='primary'
            size='md'
            className='rounded-xl px-8 py-2'
            onClick={() => void navigate({ to: '/proposals' })}
          >
            Sair
          </Button>
        </div>
      </div>
    </div>
  );
}
